import { useRef, useState, useEffect } from 'react'
import { useAudioStore } from '@/store/audioStore'
import { MUSIC_LIBRARY } from '@/config/musicLibrary'
import { EventBus, GameEvents } from '@/game/utils/EventBus'
import { Music, Play, Pause, SkipForward, SkipBack, X } from 'lucide-react'

// "Now playing" text on a single line; scrolls left (marquee) only when it
// doesn't fit. The marquee duplicates the label so translateX(-50%) loops
// seamlessly; the duplicate (and animation) only render when overflowing.
function NowPlaying({ label }: { label: string }) {
  const boxRef = useRef<HTMLDivElement>(null)
  const textRef = useRef<HTMLSpanElement>(null)
  const [scroll, setScroll] = useState(false)

  useEffect(() => {
    const box = boxRef.current
    const text = textRef.current
    if (!box || !text) return
    setScroll(text.scrollWidth > box.clientWidth + 2)
  }, [label])

  return (
    <div className="music-bar-marquee" ref={boxRef}>
      <div className={`music-bar-track${scroll ? ' scrolling' : ''}`}>
        <span className="music-bar-text" ref={textRef}>{label}</span>
        {scroll && <span className="music-bar-text" aria-hidden>{label}</span>}
      </div>
    </div>
  )
}

// A compact, always-available music control so the player can change track from
// any screen. Two layouts share the same logic:
//  - 'sidebar' → in the desktop sidebar footer (above Ajustes)
//  - 'bar'     → a slim "now playing" bar pinned above the bottom nav on mobile
export function MusicMiniPlayer({ variant, collapsed = false }: {
  variant: 'sidebar' | 'bar'
  collapsed?: boolean
}) {
  const currentTrackId = useAudioStore(s => s.currentTrackId)
  const isMusicPlaying = useAudioStore(s => s.isMusicPlaying)
  const togglePlay = useAudioStore(s => s.togglePlay)
  const nextTrack = useAudioStore(s => s.nextTrack)
  const prevTrack = useAudioStore(s => s.prevTrack)
  const miniPlayerHidden = useAudioStore(s => s.miniPlayerHidden)
  const miniPlayerHintShown = useAudioStore(s => s.miniPlayerHintShown)
  const hideMiniPlayer = useAudioStore(s => s.hideMiniPlayer)

  if (miniPlayerHidden) return null

  const track = MUSIC_LIBRARY.find(t => t.id === currentTrackId) ?? MUSIC_LIBRARY[0]

  // Hide the strip and, the first time only, remind the player where to bring
  // it back. Playback is untouched — silencing lives in Ajustes › Volumen.
  const handleClose = () => {
    if (!miniPlayerHintShown) {
      EventBus.emit(GameEvents.NOTIFY, {
        message: 'Reproductor oculto. Puedes volver a mostrarlo en Ajustes › Música.',
        type: 'info',
      })
    }
    hideMiniPlayer()
  }

  const CloseBtn = (
    <button className="mini-btn mini-close" onClick={handleClose} title="Ocultar reproductor">
      <X size={14} />
    </button>
  )

  const Disc = (
    <button
      className="mini-disc"
      data-spinning={isMusicPlaying ? 'true' : 'false'}
      onClick={togglePlay}
      title={isMusicPlaying ? 'Pausar' : 'Reproducir'}
    >
      {isMusicPlaying ? <Pause size={14} /> : <Play size={14} />}
    </button>
  )

  // --- Sidebar variant (desktop) ------------------------------------------
  if (variant === 'sidebar') {
    if (collapsed) {
      return (
        <div className="music-mini collapsed">
          {Disc}
          <button className="mini-btn" onClick={nextTrack} title="Siguiente canción">
            <SkipForward size={14} />
          </button>
        </div>
      )
    }
    return (
      <div className="music-mini sidebar">
        <span className="mini-art" data-spinning={isMusicPlaying ? 'true' : 'false'}><Music size={13} /></span>
        <div className="mini-info">
          <span className="mini-title">{track.title}</span>
          <span className="mini-mood">{track.mood}</span>
        </div>
        <div className="mini-controls">
          <button className="mini-btn" onClick={prevTrack} title="Anterior"><SkipBack size={13} /></button>
          {Disc}
          <button className="mini-btn" onClick={nextTrack} title="Siguiente"><SkipForward size={13} /></button>
          {CloseBtn}
        </div>
      </div>
    )
  }

  // --- Bar variant (mobile): slim now-playing strip above the bottom nav ---
  return (
    <div className="music-bar">
      <span className="music-bar-art"><Music size={15} /></span>
      <NowPlaying label={`${track.title} · ${track.artist}`} />
      <div className="mini-controls">
        <button className="mini-btn" onClick={prevTrack} title="Anterior"><SkipBack size={16} /></button>
        {Disc}
        <button className="mini-btn" onClick={nextTrack} title="Siguiente"><SkipForward size={16} /></button>
        {CloseBtn}
      </div>
    </div>
  )
}
